import React, { useEffect, useState, useCallback } from "react";
import styles from "../../assets/scss/Billing/AddIBillingPopup.module.scss";
import { toast } from "react-toastify";
import {
  useGetUserByIdQuery,
  useLazyGetBillingItemsQuery,
  useLazyGetItemInfoQuery,
  useUpdateBillingItemMutation,
} from "../../slices/admin/admin";
import Select from "react-select";
import WeightReader from "./WeightReader";
import WeightReaderManager from "./WeightReaderManager";
import { getUserInfo } from "../../utils/user.util";

const LiveWeightSync = ({ onWeightChange }) => {
  useEffect(() => {
    const unsubscribe = WeightReaderManager.subscribe((val) => {
      if (val && !isNaN(val)) onWeightChange(val);
    });
    return unsubscribe;
  }, [onWeightChange]);
  return null;
};

const AddIBillingPopup = ({
  onClose,
  isEditMode = false,
  itemData = null,
  editIndex = null,
}) => {
  const [formData, setFormData] = useState({
    _id: "",
    billingItemId: "",
    selectedItemId: "",
    description: "",
    quantity: "",
    price: "",
    overrideOption: false, // 🧠 Track override status for validation
    edit: false,
    manualEdit: false, // 🧠 prevents auto overwrite when user edits manually
  });
  const localUser = getUserInfo();
  const userId = localUser?._id;
  const { data: userData } = useGetUserByIdQuery(userId, { skip: !userId });

  //console.log(userData, "userData in AddIBillingPopup");





  const [errors, setErrors] = useState({});
  const [selectedItemPrices, setSelectedItemPrices] = useState({
    buyingPrice: 0,
    sellingPrice: 0,
    overridePrice: 0,
  });

  const [maxQuantity, setMaxQuantity] = useState(null);
  const [isPriceEditable, setIsPriceEditable] = useState(true);
  const [adjustedBillingItems, setAdjustedBillingItems] = useState([]); // ✅ adjusted stock list

  const [triggerBillingItems, { data: billingItemsData, isFetching: loadingBillingItems }] =
    useLazyGetBillingItemsQuery();
  const [triggerGetItemInfo] = useLazyGetItemInfoQuery();
  const [updateBillingItem] = useUpdateBillingItemMutation();

  // 🔹 Load existing item when editing
  // useEffect(() => {
  //   if (itemData) {
  //     setFormData({
  //       _id: itemData._id || itemData.itemId || "",
  //       billingItemId: itemData.billingItemId || "",
  //       selectedItemId: itemData.selectedItemId || itemData.itemId || "",
  //       description: itemData.description || "",
  //       quantity: itemData.quantity || "",
  //       price: itemData.price || "",
  //       edit: true,
  //       manualEdit: true,
  //     });
  //     setIsPriceEditable(true);
  //   }
  // }, [itemData]);

  useEffect(() => {
  if (itemData) {
    setFormData({
      _id: itemData._id || "",
      billingItemId: itemData.billingItemId || "",
      selectedItemId: itemData.selectedItemId || "",
      description: itemData.description || "",
      quantity: itemData.quantity || "",
      price: itemData.price || "",
      overrideOption: itemData.overrideOption || false,
      edit: true,
      manualEdit: true,
    });

    // THIS IS CRITICAL FOR VALIDATION
    setSelectedItemPrices({
      buyingPrice: Number(itemData.buyingPrice || 0),
      sellingPrice: Number(itemData.sellingPrice || 0),
    });

    setMaxQuantity(itemData.overallQuantity || null);
    setIsPriceEditable(!itemData.overrideOption);
  }
}, [itemData]);

  // 🔹 Load item info if editing
  useEffect(() => {
    if (formData._id) {
      triggerGetItemInfo(formData._id);
    }
  }, [formData._id, triggerGetItemInfo]);

  // 🔹 Fetch all billing items when opened
  useEffect(() => {
    triggerBillingItems({});
  }, [triggerBillingItems]);

  // ✅ Adjust available quantity based on already added items
  useEffect(() => {
    if (billingItemsData?.result) {
      const storedItems = JSON.parse(localStorage.getItem("billingItems") || "[]");

      // build used quantities by item id
      const usedQuantities = storedItems.reduce((acc, item) => {
        const id = item.selectedItemId || item._id || item.itemId;
        if (!id) return acc;
        acc[id] = (acc[id] || 0) + Number(item.quantity || 0);
        return acc;
      }, {});

      const adjustedItems = billingItemsData.result.map((item) => {
        const id = item._id;
        const used = usedQuantities[id] || 0;
        const remaining = Math.max(0, (Number(item.overallQuantity) || 0) - used);
        return { ...item, overallQuantity: remaining };
      });

      setAdjustedBillingItems(adjustedItems);
    }
  }, [billingItemsData]);

  // 🔹 Handle dropdown select
const handleDescriptionChange = (e) => {
    const selectedLabel = e.target.value;
    const selectedItem = adjustedBillingItems.find((item) => {
      const label = `${item.inventoryId?.farmerId?.firstName || ""} - Item ID ${item.itemId} - ${item.productName} - ${item.overallQuantity}kg`;
      return label === selectedLabel;
    });
    // rest stays same...

    if (selectedItem) {
      setMaxQuantity(selectedItem.overallQuantity || null);
      const buying = Number(selectedItem.buyingPrice) || 0;
      const selling = Number(selectedItem.sellingPrice) || 0;
      setSelectedItemPrices({
        buyingPrice: buying,
        sellingPrice: selling,
        overridePrice: selectedItem.overallPrice || 0,
      });

      if (selectedItem.overrideOption) {
        setIsPriceEditable(false);
        setFormData((prev) => ({
          ...prev,
          selectedItemId: selectedItem._id,
          description: selectedLabel,
          price: selectedItem.overallPrice || "",
          overrideOption: true,
        }));
      } else {
        setIsPriceEditable(true);
        setFormData((prev) => ({
          ...prev,
          selectedItemId: selectedItem._id,
          description: selectedLabel,
          price: selling || "",
          overrideOption: false,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedItemId: "",
        description: selectedLabel,
        price: "",
        overrideOption: false,
      }));
      setIsPriceEditable(true);
      setSelectedItemPrices({ buyingPrice: 0, sellingPrice: 0, overridePrice: 0 });
    }
    setErrors({});
  };

  // 🔹 Handle manual input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity") {
      if (value === "") {
        setFormData((prev) => ({ ...prev, quantity: "", manualEdit: true }));
        return;
      }
      const num = Number(value);
      if (num > 0) {
        if (maxQuantity && num > maxQuantity) {
          toast.error(`Quantity cannot exceed available stock: ${maxQuantity}`);
          return;
        }
        setFormData((prev) => ({ ...prev, quantity: value, manualEdit: true }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors({});
  };

  // 🔹 Validation before submit
  // const validate = () => {
  //   const newErrors = {};
  //   if (!formData.description) newErrors.description = "Item selection is required";
  //   if (!formData.quantity) newErrors.quantity = "Quantity is required";
  //   if (!formData.price) newErrors.price = "Price is required";
  //   return newErrors;
  // };

  // 🔹 Validation before submit
  // const validate = () => {
  //   const newErrors = {};
  //   if (!formData.description) newErrors.description = "Item selection is required";
  //   if (!formData.quantity) newErrors.quantity = "Quantity is required";
  //   if (!formData.price) newErrors.price = "Price is required";

  //   // 👇 NEW: Price cannot be below selling price
  //   const enteredPrice = Number(formData.price);
  //   const sellingPrice = Number(selectedItemPrices.buyingPrice);

  //   if (enteredPrice > 0 && sellingPrice > 0 && enteredPrice < sellingPrice) {
  //     newErrors.price = `Price cannot be below buying price (₹${sellingPrice})`;
  //   }

  //   return newErrors;
  // };

  const validate = () => {
  const newErrors = {};
  if (!formData.description) newErrors.description = "Item selection is required";
  if (!formData.quantity) newErrors.quantity = "Quantity is required";
  if (!formData.price) newErrors.price = "Price is required";

  const enteredPrice = Number(formData.price);
  const buyingPriceVal = Number(selectedItemPrices.buyingPrice); 

  // Skip buying price validation if it's an override price
  if (!formData.overrideOption && enteredPrice > 0 && buyingPriceVal > 0 && enteredPrice < buyingPriceVal) {
    newErrors.price = `Price cannot be below buying price (₹${buyingPriceVal})`;
  }

  return newErrors;
};

  // 🔹 Handle form submit
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   const validationErrors = validate();
  //   if (Object.keys(validationErrors).length > 0) {
  //     setErrors(validationErrors);
  //     return;
  //   }

  //   const storedItems = JSON.parse(localStorage.getItem("billingItems") || "[]");

  //   if (isEditMode && editIndex !== null) {
  //     if (formData._id?.trim()) {
  //       try {
  //         const res = await updateBillingItem({
  //           id: formData.billingItemId,
  //           price: formData.price,
  //         }).unwrap();
  //         toast.success(res?.message || "Price updated successfully!");
  //         storedItems[editIndex] = { ...formData };
  //         localStorage.setItem("billingItems", JSON.stringify(storedItems));
  //       } catch (err) {
  //         toast.error(err?.data?.message || "Failed to update price");
  //         return;
  //       }
  //     } else {
  //       storedItems[editIndex] = { ...formData };
  //       localStorage.setItem("billingItems", JSON.stringify(storedItems));
  //       toast.success("Item updated locally!");
  //     }
  //   } else {
  //     const newItem = { ...formData, _id: "" };
  //     localStorage.setItem("billingItems", JSON.stringify([...storedItems, newItem]));
  //     toast.success("Item added successfully!");
  //   }

  //   onClose();
  // };

  const handleSubmit = async (e) => {
  e.preventDefault();
  const validationErrors = validate();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  const storedItems = JSON.parse(localStorage.getItem("billingItems") || "[]");

  // Full item with all required fields preserved
  const fullItem = {
    // User inputs
    selectedItemId: formData.selectedItemId,
    description: formData.description,
    quantity: formData.quantity,
    price: formData.price,

    // Critical data from original item (must preserve!)
    buyingPrice: Number(itemData?.buyingPrice || selectedItemPrices.buyingPrice || 0),
    sellingPrice: Number(itemData?.sellingPrice || selectedItemPrices.sellingPrice || 0),
    overrideOption: formData.overrideOption,
    overallQuantity: itemData?.overallQuantity || maxQuantity || 0,

    // IDs
    _id: itemData?._id || "",
    billingItemId: itemData?.billingItemId || "",

    // Extra
    manualEdit: true,
  };

  if (isEditMode && editIndex !== null) {
    // Edit existing
    if (formData.billingItemId) {
      try {
        await updateBillingItem({
          id: formData.billingItemId,
          price: formData.price,
        }).unwrap();
        toast.success("Price updated on server!");
      } catch (err) {
        toast.error("Server update failed, saved locally");
      }
    }

    storedItems[editIndex] = fullItem;
    toast.success("Item updated successfully!");
  } else {
    // Add new
    storedItems.push(fullItem);
    toast.success("Item added successfully!");
  }

  localStorage.setItem("billingItems", JSON.stringify(storedItems));
  onClose(); // close popup + trigger refreshItems in parent
};

  // ✅ FIX: Stable callback for live weight updates
  const handleWeightChange = useCallback((val) => {
    const formatted = Number(val).toFixed(3);
    setFormData((prev) => {
      if (prev.manualEdit) return prev;
      if (prev.quantity === formatted) return prev;
      return { ...prev, quantity: formatted };
    });
  }, []);

  const label = formData.description || "Select Item";

  return (
    <div className={styles.createOrdersContainers}>
      <div className={styles.header}>
        <h2 className={styles.tittle}>{isEditMode ? "Edit Item" : "Add Item"}</h2>
        <span className={styles.closeIcon} onClick={onClose}>
          ✕
        </span>
      </div>

      <form className={styles.formContainer} onSubmit={handleSubmit}>
        {/* 🔸 Item Dropdown */}
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Items</label>

          {formData._id ? (
            <input type="text" className={styles.input} value={label} readOnly />
          ) : (
            <Select
              className={styles.customSelect}
              isDisabled={loadingBillingItems}
              styles={{
                control: (base) => ({
                  ...base,
                  border: "1px solid #6D6D6D",
                  borderRadius: "6px",
                  fontSize: "16px",
                  minHeight: "50px",
                  boxShadow: "none",
                  color: "black",
                  fontWeight: "600",
                }),
              }}
              value={
                formData.description
                  ? { value: formData.description, label: formData.description }
                  : null
              }
              onChange={(selectedOption) =>
                handleDescriptionChange({
                  target: { value: selectedOption ? selectedOption.value : "" },
                })
              }
              options={
                adjustedBillingItems
                  .filter((item) => item.overallQuantity > 0)
                  .map((item) => {
                    const label = `${item.inventoryId?.farmerId?.firstName || ""} - Item ID ${item.itemId
                      } - ${item.productName} - ${item.overallQuantity}kg`;
                    return { value: label, label };
                  })
              }
              placeholder={loadingBillingItems ? "Loading..." : "Searchable & Select Item"}
              isSearchable
            />
          )}
          {errors.description && <span className={styles.error}>{errors.description}</span>}
        </div>

        {/* 🔸 Quantity + Price */}
        <div className={styles.row}>
          <div className={styles.inputGroupFull}>
            <label className={styles.inputLabel}>Quantity (Auto from Weight)</label>
            <input
              name="quantity"
              className={styles.input}
              type="number"
              placeholder="Auto-filled or manual"
              value={formData.quantity}
              onChange={handleChange}
              onKeyDown={(e) => ["-", " ", "e"].includes(e.key) && e.preventDefault()}
            />
            {errors.quantity && <span className={styles.error}>{errors.quantity}</span>}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Price</label>
            <input
              name="price"
              className={styles.input}
              type="number"
              placeholder="Auto-filled from item"
              value={formData.price}
              disabled={!isPriceEditable}
              onChange={(e) => setFormData((p) => ({ ...p, price: e.target.value }))}
              onKeyDown={(e) => ["-", " ", "e"].includes(e.key) && e.preventDefault()}
            />
            {errors.price && <span className={styles.error}>{errors.price}</span>}
          </div>
        </div>



        {/* 🔸 Price Info */}
        {selectedItemPrices.buyingPrice > 0 && selectedItemPrices.sellingPrice > 0 && (
          <div className={styles.inputGroupFull}>
            {/* <h3>{`Buying: ₹${selectedItemPrices.buyingPrice} | Selling: ₹${selectedItemPrices.sellingPrice}`}</h3> */}
            <h3>{formData.overrideOption ? `Override price: ₹${selectedItemPrices.overridePrice}` : `Max selling price: ₹${selectedItemPrices.sellingPrice}`}</h3>
          </div>
        )}

        <div className={styles.footerSection}>
          {/* 🔸 Live Weight Integration */}
          {/* <div>
            <LiveWeightSync onWeightChange={handleWeightChange} />
            <WeightReader />
          </div> */}

          {userData?.result?.[0]?.isRS232Enabled && (
            <div className={styles.weightSection}>
              <LiveWeightSync onWeightChange={handleWeightChange} />
              <WeightReader />
            </div>
          )}

          {/* 🔸 Footer */}
          <div className={styles.footerButtons}>
            <button type="button" className={styles.cancel} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.save}>
              {isEditMode ? "Update" : "Add"}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default AddIBillingPopup;



